import { ref } from 'vue'

/**
 * Bilingual, French by default — the same choice the customer app makes, and the
 * language the sales team works in.
 *
 * Keys read like the sentence they produce. The console deliberately never shows
 * a raw server enum (`IN_REVIEW`, `REQUESTED`, `CREDIT`): those are how the
 * database thinks, not how a salesperson describes a customer. Every one of them
 * is translated through `status.*` below.
 */
export type Locale = 'fr' | 'en'

export const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
]

/**
 * 🔴 The key set is FRENCH's, declared literal and checked with `satisfies`; `en`
 * is typed against it. A key present in one dictionary and not the other fails
 * `npm run build` instead of falling back to the other language on screen.
 */
const fr = {
  'app.name': 'Espace commercial',
  'app.signOut': 'Se déconnecter',
  'app.language': 'Changer de langue',
  'app.search': 'Rechercher un client…',
  'app.retry': 'Réessayer',
  'app.loading': 'Chargement…',
  'app.back': 'Retour',
  'app.cancel': 'Annuler',
  'app.none': 'Aucun',

  'nav.home': 'Accueil',
  'nav.customers': 'Clients',
  'nav.treasury': 'Trésorerie',
  'nav.pipeline': 'Parcours des fonds',
  'app.refresh': 'Actualiser',
  'app.menu': 'Menu',
  'nav.groupMoney': 'Argent',
  'nav.groupPeople': 'Personnes',

  /* referrals (2026-09-25) — paid automatically on the invitee's KYC, monitored here */
  'nav.referrals': 'Parrainage',
  'cust.tabDirectory': 'Annuaire',
  'nav.transfers': 'Virements',

  'tr.title': 'Virements entre clients',
  'tr.subtitle': 'Envois instantanés d’USDT d’un client Tothor à un autre. Définitifs : il n’existe aucun retour en arrière, cet écran ne fait que montrer.',
  'tr.limits': 'Plafonds : {per} par virement · {daily} par 24 h et par expéditeur',
  'tr.kpiCount': 'Virements',
  'tr.kpiVolume': 'Volume total',
  'tr.kpi24h': 'Volume 24 h',
  'tr.kpiSenders': 'Expéditeurs',
  'tr.colWhen': 'Date',
  'tr.colFrom': 'De',
  'tr.colTo': 'Vers',
  'tr.colAmount': 'Montant',
  'tr.colNote': 'Message',
  'tr.searchPh': 'Nom, e-mail ou message…',
  'tr.none': 'Aucun virement entre clients pour l’instant.',
  'ref.title': 'Parrainage',
  'ref.subtitle':
    'Chaque client peut inviter d’autres personnes. Le parrain reçoit {quantity} (jetons bloqués 12 mois, hors créances USDT) dès que l’identité de la personne invitée est vérifiée — automatiquement. Cette page surveille le programme et permet d’annuler une prime.',
  'ref.kpiTotal': 'Parrainages',
  'ref.kpiPending': 'En attente de KYC',
  'ref.kpiPaid': 'Primes versées',
  'ref.kpiPaidQty': 'TTR distribués',
  'ref.kpiReferrers': 'Parrains',
  'ref.tabReferrals': 'Tous les parrainages',
  'ref.tabReferrers': 'Par parrain',
  'ref.colReferrer': 'Parrain',
  'ref.colReferee': 'Filleul',
  'ref.colRefereeKyc': 'KYC du filleul',
  'ref.colStatus': 'Prime',
  'ref.colBonus': 'Montant',
  'ref.colDate': 'Inscrit le',
  'ref.colInvited': 'Invités',
  'ref.colPaid': 'Payés',
  'ref.colPending': 'En attente',
  'ref.colRevoked': 'Annulés',
  'ref.colEarned': 'Gagné',
  'ref.filterAll': 'Tous',
  'ref.revoke': 'Annuler la prime',
  'ref.revokePending': 'Bloquer',
  'ref.revokeTitle': 'Annuler ce parrainage',
  'ref.revokeBodyPaid':
    'Retire {quantity} de l’épargne de {who} (écriture de correction). Irréversible depuis la console. Le client voit « Correction: referral bonus reversed » dans son historique ; votre motif n’est jamais montré.',
  'ref.revokeBodyPending':
    'Ce parrainage ne versera jamais de prime, même si le filleul est vérifié ensuite. Votre motif n’est jamais montré au client.',
  'ref.revokeReason': 'Motif (interne)',
  'ref.revokeReasonPh': 'ex. comptes en double, même personne',
  'ref.revokeConfirm': 'Annuler la prime',
  'ref.revokedBy': 'Annulé par {who} : {reason}',
  'ref.suspended': 'suspendu',
  'ref.emailUnverified': 'e-mail non vérifié',
  'ref.searchPh': 'Filtrer par e-mail, nom ou code…',
  'status.PAID': 'Versée',
  'status.REVOKED': 'Annulée',

  /* pipeline — the money's path, not any one account's */
  'pipe.title': 'Parcours des fonds',
  'pipe.subtitle':
    'Chaque étape que traversent les fonds d’un client, avec le solde qui s’y trouve en ce moment. À lire de gauche à droite : un écart entre deux étapes, ce sont des fonds en transit.',
  'pipe.usdtIn': 'Dépôt USDT direct',
  'pipe.usdtInSub': 'Le client envoie sur sa propre adresse, puis un job root transfère vers la trésorerie — ni custody partenaire, ni sweep.',
  'pipe.usdtInPending': 'adresse(s) à transférer',
  'pipe.hop1': '1 · Custody partenaire',
  'pipe.hop2': '2 · Trésorerie Tothor',
  'pipe.hop3': '3 · Après conversion',
  'pipe.hop4': '4 · Réserve carte',
  'pipe.hop1sub': 'SwipeLux · non crédité',
  'pipe.hop2sub': 'USDC on-chain',
  'pipe.hop3sub': 'USDT disponible',
  'pipe.hop4sub': 'HyperPay · doit rester ~0',
  'pipe.sweep': 'transfert',
  'pipe.swap': 'conversion',
  'pipe.topup': 'alimentation',
  'pipe.owed': 'Dû aux clients',
  'pipe.tracked': 'dont indexé :',
  'pipe.held': 'Détenu au total',
  'pipe.inflight': 'En transit',
  'pipe.stuck': 'Bloqué',
  'pipe.openItems': 'Transferts en cours',
  'pipe.none': 'Rien en transit — chaque transfert est arrivé.',
  'pipe.age': 'Ancienneté',
  'pipe.unreadable': 'illisible',
  'pipe.poolResting':
    'La réserve carte détient un solde au repos. C’est un compte de passage : il devrait être proche de zéro entre deux opérations. Un solde qui stagne signifie que des fonds ont quitté la trésorerie sans débit correspondant.',

  'home.attention': 'À traiter',
  'home.attentionEmpty': 'Rien en attente. Tout est à jour.',
  'home.attentionNote': 'Ces clients attendent une décision de votre part.',
  'home.overview': 'Vue d’ensemble',
  'home.totalHeld': 'Solde total des clients',

  'seg.needs_verification': 'À vérifier',
  'seg.no_card': 'Sans carte',
  'seg.active': 'Actifs',
  'seg.dormant': 'Dormants',
  'seg.all': 'Tous',
  'seg.needs_verification.hint': 'Identité non confirmée (Tothor) ou e-mail non vérifié (1cards) — ils ne peuvent pas encore tout faire.',
  'seg.no_card.hint': 'Vérifiés mais sans carte. À relancer.',
  'seg.active.hint': 'Carte en main et compte utilisé.',
  'seg.dormant.hint': 'Aucun mouvement depuis 60 jours.',

  'cust.since': 'Client depuis {when}',
  'cust.balance': 'Solde',
  'cust.savings': 'Épargne (non disponible)',
  'cust.cards': 'Cartes',
  'cust.pending': 'En attente',
  'cust.colCustomer': 'Client',
  'cust.colKyc': 'Vérification',
  'cust.colSpendable': 'Disponible',
  'cust.colTtr': 'Jeton TTR',
  'cust.colGold': 'Or (oz · g)',
  'cust.colBtc': 'Bitcoin (BTC)',
  'cust.colCards': 'Cartes',
  'cust.colJoined': 'Inscrit le',
  'cust.colDeposits': 'Dépôts',
  'cust.granted': 'attribué',
  'cust.connect': 'Se connecter',
  'cust.connecting': 'Ouverture…',
  'cust.noBalance': 'Aucun solde',
  'cust.tab.activity': 'Activité',
  'cust.tab.cards': 'Cartes',
  'cust.tab.deposits': 'Dépôts',
  'cust.tab.withdrawals': 'Retraits',
  'cust.tab.identity': 'Identité',
  'cust.tab.notes': 'Notes',
  'cust.tab.partners': 'Partenaires',
  'vnd.sumsub': 'Sumsub — vérification d’identité',
  'vnd.swipelux': 'SwipeLux — dépôts par carte',
  'vnd.hypercard': 'HyperCard — carte',
  'vnd.readonly': 'lecture seule',
  'vnd.noApplicant': 'Aucun dossier Sumsub pour ce client.',
  'vnd.extracted': 'Lu sur les documents',
  'vnd.submitted': 'Transmis par Tothor',
  'vnd.showDocs': 'Afficher les pièces d’identité',
  'vnd.docsNote': 'Chaque consultation est enregistrée à votre nom.',
  'vnd.noDocs': 'Aucune image disponible.',
  'vnd.noSwxCustomer': 'Aucun client SwipeLux — il est créé au premier devis, jamais à l’inscription.',
  'vnd.custody': 'Détenu chez le partenaire',
  'vnd.empty': 'Vide.',
  'vnd.more': 'Rails, capacités et comptes',
  'vnd.txns': '{n} opérations (60 jours)',
  'vnd.application': 'Demande de carte',
  'vnd.noPan': 'Le numéro de carte et le CVV ne sont pas affichés ici : le titulaire les lit dans son application.',
  'cust.noActivity': 'Aucune opération pour l’instant.',
  'cust.noCards': 'Aucune carte.',
  'card.freeze': 'Geler la carte',
  'card.unfreeze': 'Dégeler la carte',
  'card.freezeTitle': 'Geler la carte •••• {last4} ?',
  'card.unfreezeTitle': 'Dégeler la carte •••• {last4} ?',
  'card.freezeBody': 'La demande part chez l’émetteur. Une fois confirmée, la carte refuse tout paiement jusqu’à ce qu’elle soit dégelée.',
  'card.unfreezeBody': 'La demande part chez l’émetteur. Une fois confirmée, la carte accepte de nouveau les paiements.',
  'card.freezeRequested': 'Gel demandé — la carte change d’état quand l’émetteur confirme.',
  'card.unfreezeRequested': 'Dégel demandé — la carte change d’état quand l’émetteur confirme.',
  'card.refresh': 'Actualiser',
  'card.issuerFrozen': 'Gelée par l’émetteur — ne peut pas être dégelée d’ici.',
  'cust.noDeposits': 'Aucun dépôt.',
  'cust.noWithdrawals': 'Aucun retrait.',
  'cust.noNotes': 'Aucune note.',
  'cust.suspended': 'Compte suspendu',
  'cust.emailUnverified': 'E-mail non confirmé',
  'cust.earlierReason': 'Motif (tentative précédente)',
  'kyc.restart': 'Relancer la vérification',
  'kyc.restartHint': 'Réinitialise la vérification Sumsub pour que le client envoie une nouvelle pièce d’identité et un selfie. Un client vérifié garde l’accès jusqu’au nouveau verdict. Le client est prévenu par e-mail.',
  'kyc.restartTitle': 'Relancer la vérification d’identité',
  'kyc.restartBody': 'Le client devra refaire la photo de sa pièce d’identité et un selfie. S’il est déjà vérifié, il garde l’accès à son compte jusqu’au nouveau verdict. Il reçoit un e-mail l’invitant à renvoyer ses documents.',
  'kyc.restartNote': 'Note interne (facultatif)',
  'kyc.restartPlaceholder': 'ex. photo floue, pièce expirée',
  'kyc.reopenedNote': 'Vérification rouverte — en attente des nouveaux documents. Le client reste vérifié en attendant.',
  'pin.set': 'Définir le code',
  'pin.short': 'Code PIN',
  'pin.saved': 'Code enregistré ✓',
  'pin.change': 'Changer le code',
  'pin.on': 'Code actif',
  'pin.off': 'Aucun code',
  'pin.locked': 'Code bloqué',
  'pin.done': 'Code enregistré — client prévenu par e-mail',
  'pin.title': 'Code à 6 chiffres du client',
  'pin.body': 'Remplace le code qui déverrouille l’application du client et lève tout blocage. Le client reçoit un e-mail indiquant que le support l’a modifié. Communiquez-lui le code, puis invitez-le à choisir le sien.',
  'pin.new': 'Nouveau code',
  'pin.again': 'Confirmer le code',
  'pin.mismatch': 'Les deux codes ne correspondent pas.',
  'pin.cancel': 'Annuler',
  'pin.save': 'Enregistrer le code',
  'pin.saving': 'Enregistrement…',
  'cust.lastSeen': 'Dernière opération',
  'cust.never': 'jamais',
  'cust.contact': 'Coordonnées',
  'cust.cardEnding': 'Carte •••• {last4}',

  'act.approve': 'Approuver',
  'act.refuse': 'Refuser',
  'act.autoPaying': 'Envoi automatique sur Polygon',
  'act.noPayoutRight': 'Décision réservée aux administrateurs',
  'act.autoApproved': 'approuvé automatiquement',
  'act.payoutFailed': 'Envoi automatique échoué — les fonds restent réservés. Relancez l’envoi, ou refusez pour les rendre au client.',
  'act.retry': 'Relancer l’envoi',
  'act.retryHint': 'Relancer n’est accepté que si la blockchain confirme que rien n’est parti.',
  'act.approveIdentity': 'Valider l’identité',
  'act.reason': 'Motif communiqué au client',
  'act.reasonPlaceholder': 'Cette adresse de destination n’a pas pu être vérifiée.',
  'act.confirmRefuse': 'Refuser ce retrait',
  'act.refuseBody':
    'L’argent revient immédiatement sur le solde du client. Il verra le motif que vous indiquez.',
  'act.available': 'Solde disponible',
  'act.requestedAgo': 'Demandé {age}',
  'act.to': 'Vers',

  'status.VERIFIED': 'Vérifié',
  'status.NOT_STARTED': 'Non commencé',
  'status.PENDING': 'En cours',
  'status.SUBMITTED': 'Documents envoyés',
  'status.IN_REVIEW': 'En cours d’examen',
  'status.REJECTED': 'Refusé',
  'status.REQUESTED': 'À approuver',
  'status.APPROVED': 'Prêt à envoyer',
  'status.SENT': 'Envoyé',
  'status.SENDING': 'Envoi en cours',
  'status.PAYOUT_FAILED': 'Envoi échoué',
  'status.RETURNED': 'Revenu',
  /* ⚠️ Added with the USDT deposit flow. A status with no key here renders as
     the literal 'status.CREDITED' — visible rather than blank, because `t()`
     falls back to the key, but still wrong on an operator's screen. Caught by
     confirming a deposit and looking at the chip. */
  'status.CREDITED': 'Crédité',
  'status.COMPLETED': 'Terminé',
  'status.CANCELLED': 'Annulé',
  'status.FAILED': 'Échoué',
  'status.ACTIVE': 'Active',
  'status.BLOCKED': 'Bloquée',

  'kind.payout': 'Retrait',
  'kind.identity': 'Vérification d’identité',
  'money.in': 'Entrée',
  'money.out': 'Sortie',

  'signin.subtitle': 'Suivi des clients. Réservé à l’équipe.',
  'signin.email': 'E-mail',
  'signin.password': 'Mot de passe',
  'signin.submit': 'Se connecter',
  'signin.working': 'Connexion…',
  'signin.audit': 'Chaque action est enregistrée à votre nom.',

  /* environment banner — absent on production, by design */
  'env.banner': 'Environnement {env} — ce ne sont pas des données réelles',
  'env.unknown': 'Environnement inconnu — impossible de joindre l’API',

  /* treasury — the money screen. Wording matters here: the shared card pool is
     the CARDHOLDER's money in transit, never Tothor's. */
  'treasury.title': 'Trésorerie',
  'treasury.subtitle': 'Ce que les clients ont en dépôt, face à ce que Tothor détient réellement.',
  'treasury.loading': 'Chargement…',
  'treasury.buffer': 'marge',
  'treasury.owed': 'Dû aux clients',
  'treasury.onchain': 'Fonds clients on-chain',
  'treasury.holds': 'Blocages en cours',
  'treasury.orphans': 'Lignes non attribuées',
  'treasury.orphansWarning':
    'Lignes comptables sans propriétaire — Tothor détient de l’argent qu’il ne peut attribuer à aucun client.',
  'treasury.chainUnreadable':
    'chaîne illisible : ce montant est inconnu, pas nul.',
  'treasury.poolTitle': 'Réserve carte partagée — fonds des porteurs en transit',
  // Carries what the API's own `note` field says, so the console no longer
  // renders that English sentence on a French screen.
  'treasury.poolNote':
    'Fonds de passage appartenant aux porteurs — proche de 0 au repos. Elle est alimentée à la demande, à partir du solde du porteur lui-même, et se vide à mesure que la carte est émise : elle est donc bien comptée dans le total de solvabilité ci-dessus. Il n’y a pas d’argent de la société dans ce produit. ⚠️ Un solde qui stagne ici signifie que des fonds ont quitté la trésorerie sans débit correspondant.',
  'treasury.floor': 'seuil',
  'treasury.issuingOn': 'émission de cartes ACTIVE',
  'treasury.issuingOff': 'émission de cartes INACTIVE',
  'treasury.poolEmpty':
    'Vide — aucune carte ne peut être émise ou rechargée tant qu’elle n’est pas alimentée, quel que soit le solde du client.',
  'treasury.sweeps': 'Transferts (sweeps)',
  'treasury.inFlight': '{n} en cours',
  'treasury.noSweeps': 'Aucun transfert pour l’instant.',
  'treasury.thCustomer': 'Client',
  'treasury.thStatus': 'Statut',
  'treasury.thRequested': 'Demandé',
  'treasury.thConfirmed': 'Confirmé',
  'treasury.thProof': 'Preuve',
  'cust.depositBlocked': 'Ce client ne peut pas déposer par carte',
  'cust.depositBlockedBody': 'Notre partenaire de paiement refuse tant qu’il manque :',
  'cust.depositBlockedNote':
    'Le client peut les renseigner lui-même dans ses Réglages. Le dépôt en USDT n’est pas concerné.',
  'cust.field.street': 'adresse',
  'cust.field.city': 'ville',
  'cust.field.post_code': 'code postal',
  'dep.seenBefore': '· déjà vue',
  'dep.title': 'Dépôts USDT',
  'dep.none': 'Aucun dépôt USDT pour l’instant.',
  'dep.why':
    'Chaque client a sa propre adresse : un transfert finalisé sur Polygon crédite son propriétaire automatiquement, avec le montant lu sur la chaîne. Rien à confirmer ici.',
  'dep.from': 'Envoyé depuis',
  'dep.to': 'Reçu sur',
  'dep.stock': 'Adresses prêtes',
  'dep.assigned': 'Adresses attribuées',
  'dep.awaitingForward': 'En attente de transfert vers la trésorerie',
  'dep.stockEmpty':
    'Aucune adresse prête : les nouveaux clients ne peuvent pas recevoir d’adresse de dépôt tant que deposit_forward.py (crontab root) n’a pas tourné.',
  'dep.inAddresses': 'dont sur les adresses de dépôt clients',
  'dep.address': 'Adresse de dépôt USDT',
  'dep.noAddress': 'Pas encore d’adresse — le client n’a pas ouvert la page de dépôt USDT.',
  'dep.forwardPending': 'USDT crédités, pas encore transférés à la trésorerie',
  'treasury.retry': 'Relancer',
  'treasury.retrying': 'Relance…',

  /* 🔴 L'épargne est indexée sur un cours : le client reprend sa QUANTITÉ au
     cours du jour, donc cette part de la créance n'est couverte par aucun actif
     de la trésorerie. Elle est isolée pour que l'opérateur sache quelle part de
     la marge peut bouger seule pendant la nuit. */
  'treasury.claimUnknown': 'indéterminé',
  'treasury.claimUnknownNote':
    "tant qu'une position d'épargne ne peut pas être valorisée, le total dû est indéterminé — il n'est pas nul.",
  'treasury.trackedTitle': 'Dont épargne indexée :',
  'treasury.grantedTitle': 'Jetons attribués (non achetés — dus en jetons, hors créance USDT) :',
  'treasury.trackedNote':
    "Non couverte par la trésorerie. Tothor porte le risque de cours : si l'or monte, la créance monte avec lui.",
  'treasury.unpriceable': 'cours indisponible',

  /* solvency verdict — a server enum, so it goes through the dictionary too */
  'verdict.SOLVENT': 'SOLVABLE',
  'verdict.SHORTFALL': 'DÉFICIT',
  'verdict.UNKNOWN': 'INDÉTERMINÉ',

  /* sweep states. A separate namespace from `status.*` on purpose: a sweep's
     PENDING means "rien n’a été envoyé", which is not what PENDING means for a
     KYC file. Sharing the key would let a change to one silently retitle the
     other. The SENT / CONFIRMED distinction is load-bearing — SENT is only the
     partner's claim, and no balance is credited until CONFIRMED. */
  'sweep.PENDING': 'Créé — rien envoyé',
  'sweep.QUOTED': 'Devis obtenu',
  'sweep.SUBMITTED': 'Transmis au partenaire',
  'sweep.SENT': 'Envoyé — non confirmé',
  'sweep.CONFIRMED': 'Confirmé on-chain',
  'sweep.ACTION_REQUIRED': 'Action requise',
  'sweep.NEEDS_REVIEW': 'À vérifier',
  'sweep.FAILED': 'Échoué',
  'sweep.ABANDONED': 'Abandonné',

  /* brands (2026-10-14) — the console's Global / Tothor / 1cards view */
  'brand.view': 'Marque affichée',
  'brand.all': 'Global',
  'brand.tothor': 'Tothor',
  'brand.1cards': '1cards',
  'brand.sharedPot': 'Vue {brand} : ce qui est dû est celui des clients {brand}. La trésorerie et le pool de cartes sont communs aux deux marques — ils sont les mêmes dans chaque vue, et seule la vue Globale dit s’ils couvrent ce qui est dû.',
  'brand.noVerdict': 'Pas de verdict de solvabilité hors vue Globale',
  'brand.noKyc': '1cards ne vérifie pas l’identité',

  /* team — operator accounts (2026-10-14), SUPER_ADMIN only */
  'nav.team': 'Équipe',
  'nav.account': 'Mon compte',
  'team.title': 'Équipe',
  'team.subtitle': 'Les comptes qui ont accès à cette console : qui ils sont, leur rôle, ce que ce rôle permet, et ce qu’ils ont fait.',
  'team.tabOperators': 'Opérateurs',
  'team.tabRoles': 'Rôles et permissions',
  'team.new': 'Nouvel opérateur',
  'team.kpiActive': 'Comptes actifs',
  'team.kpiSuper': 'Super administrateurs',
  'team.kpiInactive': 'Désactivés',
  'team.searchPh': 'Nom ou e-mail…',
  'team.colOperator': 'Opérateur',
  'team.colRole': 'Rôle',
  'team.colStatus': 'Statut',
  'team.colLastLogin': 'Dernière connexion',
  'team.colActions': 'Actions',
  'team.never': 'Jamais',
  'team.you': 'vous',
  'team.active': 'Actif',
  'team.inactive': 'Désactivé',
  'team.open': 'Ouvrir',
  'team.actionsCount': '{n} action(s)',
  'team.rolesIntro': 'Les permissions sont attachées au rôle, pas à la personne : changer le rôle d’un opérateur change tout ce qu’il peut faire, dès sa requête suivante. Seules les permissions qu’un écran ou une action vérifie réellement sont listées.',
  'team.permission': 'Permission',
  'team.allows': 'Ce qu’elle permet',

  'role.SUPER_ADMIN': 'Super administrateur',
  'role.ADMIN': 'Administrateur',
  'role.SUPPORT': 'Support',
  'role.VIEWER': 'Lecture seule',
  'roleDesc.SUPER_ADMIN': 'Tout, y compris les mouvements de trésorerie et la gestion de l’équipe.',
  'roleDesc.ADMIN': 'Le travail quotidien : clients, KYC, retraits. Ni trésorerie, ni équipe.',
  'roleDesc.SUPPORT': 'Répondre aux clients : voir leur dossier et leurs pièces, sans rien décider.',
  'roleDesc.VIEWER': 'Tout voir — clients, trésorerie — sans rien pouvoir changer.',

  'perm.users:read': 'Voir les clients',
  'perm.users:write': 'Agir sur un compte client',
  'perm.kyc:read': 'Voir les pièces d’identité',
  'perm.kyc:write': 'Décider d’une vérification',
  'perm.cards:write': 'Agir sur une carte',
  'perm.finance:read': 'Voir la trésorerie',
  'perm.withdrawals:write': 'Décider d’un retrait',
  'perm.finance:write': 'Mouvements de trésorerie',
  'perm.admins:write': 'Gérer l’équipe',
  'permDesc.users:read': 'Annuaire, fiche client, données partenaires, parrainage.',
  'permDesc.users:write': 'Se connecter en tant que client, définir son code PIN.',
  'permDesc.kyc:read': 'Données et photos de vérification Sumsub.',
  'permDesc.kyc:write': 'Valider un dossier, relancer une vérification.',
  'permDesc.cards:write': 'Demander à l’émetteur de geler ou dégeler une carte.',
  'permDesc.finance:read': 'Trésorerie, parcours des fonds, virements, dépôts.',
  'permDesc.withdrawals:write': 'Approuver, refuser ou relancer un retrait.',
  'permDesc.finance:write': 'Relancer un transfert de fonds, annuler une prime de parrainage.',
  'permDesc.admins:write': 'Cet écran : créer, modifier, désactiver des opérateurs.',

  'op.back': 'Équipe',
  'op.profile': 'Profil',
  'op.firstName': 'Prénom',
  'op.lastName': 'Nom',
  'op.email': 'E-mail',
  'op.role': 'Rôle',
  'op.save': 'Enregistrer',
  'op.saved': 'Enregistré.',
  'op.created': 'Créé le {date}',
  'op.lastLogin': 'Dernière connexion : {date}',
  'op.access': 'Accès',
  'op.activeBody': 'Ce compte peut se connecter.',
  'op.inactiveBody': 'Ce compte ne peut plus se connecter. Son historique reste à son nom.',
  'op.deactivate': 'Désactiver',
  'op.reactivate': 'Réactiver',
  'op.deactivateTitle': 'Désactiver {name} ?',
  'op.deactivateBody': 'Le compte est coupé immédiatement : sa prochaine requête est refusée. Son historique reste intact et vous pourrez le réactiver.',
  'op.password': 'Mot de passe',
  'op.passwordBody': 'Donnez un nouveau mot de passe à cet opérateur, puis transmettez-le-lui par un canal sûr. Une session déjà ouverte n’est pas coupée (elle expire en 15 minutes au plus) : en cas de compte compromis, désactivez-le d’abord.',
  'op.passwordNew': 'Nouveau mot de passe',
  'op.passwordSet': 'Définir le mot de passe',
  'op.passwordDone': 'Mot de passe changé. Transmettez-le à l’opérateur.',
  'op.generate': 'Générer',
  'op.copy': 'Copier',
  'op.copied': 'Copié',
  'op.passwordMin': 'Au moins {n} caractères.',
  'op.activity': 'Activité',
  'op.activityNone': 'Aucune action enregistrée.',
  'op.delete': 'Supprimer le compte',
  'op.deleteBody': 'Réservé à un compte créé par erreur et jamais utilisé. Un opérateur qui s’est déjà connecté ou a déjà agi se désactive : la suppression rendrait son historique anonyme.',
  'op.deleteConfirmTitle': 'Supprimer {name} ?',
  'op.deleteConfirmBody': 'Définitif. Tapez l’adresse e-mail de l’opérateur pour confirmer.',
  'op.deleteBlocked': 'Ce compte a un historique : désactivez-le plutôt.',
  'op.self': 'C’est votre compte : votre rôle et votre accès se changent par un autre super administrateur. Votre nom et votre mot de passe se changent dans Mon compte.',
  'op.newTitle': 'Nouvel opérateur',
  'op.newBody': 'Le compte est actif dès sa création. Transmettez le mot de passe par un canal sûr.',
  'op.create': 'Créer le compte',

  'acct.title': 'Mon compte',
  'acct.subtitle': 'Votre nom, votre mot de passe, et ce que votre rôle vous permet.',
  'acct.yourRole': 'Votre rôle',
  'acct.currentPassword': 'Mot de passe actuel',
  'acct.newPassword': 'Nouveau mot de passe',
  'acct.confirmPassword': 'Confirmer le nouveau mot de passe',
  'acct.mismatch': 'Les deux mots de passe ne correspondent pas.',
  'acct.change': 'Changer le mot de passe',
  'acct.changed': 'Mot de passe changé. Il servira à votre prochaine connexion.',

  'act.ADMIN_LOGIN': 'Connexion',
  'act.ADMIN_CREATE': 'A créé un opérateur',
  'act.ADMIN_UPDATE': 'A modifié un opérateur',
  'act.ADMIN_DELETE': 'A supprimé un opérateur',
  'act.ADMIN_PASSWORD_RESET': 'A changé le mot de passe d’un opérateur',
  'act.ADMIN_PASSWORD_CHANGE': 'A changé son mot de passe',
  'act.ADMIN_PROFILE_UPDATE': 'A modifié son profil',
  'act.KYC_APPLICATION_APPROVE': 'A validé un KYC',
  'act.KYC_APPLICATION_REJECT': 'A refusé un KYC',
  'act.KYC_DOCUMENTS_VIEW': 'A consulté des pièces d’identité',
  'act.REFERRAL_REVOKE': 'A annulé une prime de parrainage',
  'act.USER_EMAIL_CHANGE': 'A changé l’e-mail d’un client',
  'act.USER_EMAIL_VENDOR_REPAIR': 'A réparé l’e-mail d’un client chez un partenaire',
  'act.USER_FORCE_VERIFY_KYC': 'A forcé la vérification d’un client',
  'act.USER_IMPERSONATE': 'S’est connecté en tant que client',
  'act.USER_KYC_RESTART': 'A relancé la vérification d’un client',
  'act.USER_PIN_SET': 'A défini le code PIN d’un client',
  'act.CARD_FREEZE_REQUESTED': 'A demandé le gel d’une carte',
  'act.CARD_UNFREEZE_REQUESTED': 'A demandé le dégel d’une carte',
  'act.USER_SUSPEND': 'A suspendu un client',
  'act.USER_UNSUSPEND': 'A réactivé un client',

  // Prospects (2026-11-01)
  'nav.groupSales': 'Ventes',
  'nav.prospects': 'Prospects',
  'perm.prospects:read': 'Voir les prospects',
  'perm.prospects:write': 'Travailler les prospects',
  'permDesc.prospects:read': 'Liste des prospects Tothor, indicateurs et historique.',
  'permDesc.prospects:write': 'Notes, statut, relances.',
  'pr.title': 'Prospects',
  'pr.subtitle': 'Les contacts que l’équipe commerciale travaille — pas encore clients. Propres à Tothor.',
  'pr.count': '{n} prospects',
  'pr.add': 'Ajouter un prospect',
  'pr.onlyTothor': 'Les prospects appartiennent à Tothor',
  'pr.onlyTothorBody': 'Cet espace n’existe que dans la vue Tothor — ni en Global, ni chez 1cards.',
  'pr.switchTothor': 'Passer à la vue Tothor',
  'pr.kpiTotal': 'Prospects',
  'pr.kpiDue': 'Relances dues',
  'pr.ofTotal': '{p} du total',
  'pr.dueHint': 'à faire aujourd’hui ou en retard',
  'pr.accountNotMarked': '{n} prospect(s) ont ouvert un compte Tothor mais ne sont marqués ni « Compte ouvert » ni convertis.',
  'pr.showThem': 'Les afficher',
  'pr.chStatus': 'Par statut',
  'pr.chActivity': 'Activité — 30 derniers jours',
  'pr.chActivitySub': 'Notes et changements enregistrés par jour.',
  'pr.chAge': 'Par âge',
  'pr.chAgeSub': 'Calculé depuis l’année de naissance du fichier.',
  'pr.seriesNotes': 'Notes',
  'pr.seriesChanges': 'Changements',
  'pr.seriesConversions': 'conversion(s)',
  'pr.ageUnknown': 'Inconnu',
  'pr.tableView': 'Voir les chiffres',
  'pr.chartView': 'Voir le graphique',
  'pr.searchPh': 'Nom, e-mail, téléphone…',
  'pr.filterAll': 'Tous',
  'pr.onlyDue': 'Relances dues',
  'pr.onlyAccount': 'Ont un compte',
  'pr.sortBy': 'Trier par',
  'pr.sortUpdated': 'Dernière modification',
  'pr.sortCreated': 'Date d’ajout',
  'pr.colCreated': 'Ajouté',
  'pr.colName': 'Prospect',
  'pr.colPhone': 'Téléphone',
  'pr.colAge': 'Âge',
  'pr.colStatus': 'Statut',
  'pr.colFollowUp': 'Relance',
  'pr.colNotes': 'Notes',
  'pr.colUpdated': 'Modifié',
  'pr.none': 'Aucun prospect.',
  'pr.noMatch': 'Aucun prospect ne correspond.',
  'pr.page': '{from}–{to} sur {n}',
  'pr.prev': 'Précédent',
  'pr.next': 'Suivant',
  'pr.years': '{n} ans',
  'pr.hasAccount': 'A un compte',
  'pr.status.NEW': 'À appeler',
  'pr.status.NRP_1': 'NRP 1',
  'pr.status.NRP_2': 'NRP 2',
  'pr.status.NRP_3': 'NRP 3',
  'pr.status.INTERESTED_HIGH': 'Intéressé — fort potentiel',
  'pr.status.INTERESTED_LOW': 'Intéressé — faible potentiel',
  'pr.status.INTERESTED': 'Intéressé (à qualifier)',
  'pr.status.CALLBACK': 'À rappeler',
  'pr.status.ACCOUNT_OPENED': 'Compte ouvert',
  'pr.status.CONVERTED': 'Converti',
  'pr.status.NOT_INTERESTED': 'Pas intéressé',
  'pr.status.INVALID': 'Coordonnées invalides',
  // Statuts et intérêts d’avant le 2026-11-03 — l’historique les porte encore.
  'pr.status.NO_ANSWER': 'Sans réponse',
  'pr.status.CONTACTED': 'Contacté',
  'pr.status.FOLLOW_UP': 'À relancer',
  'pr.status.MEETING': 'Rendez-vous',
  'pr.status.CLOSED': 'Clos',
  'pr.interest.UNKNOWN': 'Inconnu',
  'pr.interest.INTERESTED': 'Intéressé',
  'pr.interest.NOT_INTERESTED': 'Pas intéressé',
  'pr.markAccountOpened': 'Marquer « Compte ouvert »',
  'pr.back': 'Prospects',
  'pr.contact': 'Coordonnées',
  'pr.email': 'E-mail',
  'pr.phone': 'Téléphone',
  'pr.birth': 'Naissance',
  'pr.source': 'Origine',
  'pr.added': 'Ajouté le',
  'pr.lastContact': 'Dernier contact',
  'pr.pipeline': 'Suivi',
  'pr.status': 'Statut',
  'pr.interest': 'Intérêt',
  'pr.followUp': 'Prochaine relance',
  'pr.clearFollowUp': 'Effacer',
  'pr.accountOpened': 'Ce prospect a ouvert un compte Tothor.',
  'pr.openClient': 'Ouvrir la fiche client',
  'pr.notes': 'Notes et historique',
  'pr.notePh': 'Appel, réponse, objection, prochaine étape…',
  'pr.addNote': 'Ajouter la note',
  'pr.noActivity': 'Aucune activité.',
  'pr.by': 'par {who}',
  'pr.system': 'import',
  'pr.act.CREATED': 'Ajouté à la liste ({src})',
  'pr.act.STATUS': 'Statut : {from} → {to}',
  'pr.act.INTEREST': 'Intérêt : {from} → {to}',
  'pr.act.CONVERTED_ON': 'Marqué converti en client',
  'pr.act.CONVERTED_OFF': 'Conversion retirée',
  'pr.act.FOLLOW_UP': 'Relance prévue le {to}',
  'pr.act.FOLLOW_UP_CLEAR': 'Relance effacée',
  'pr.readOnly': 'Lecture seule — votre rôle ne permet pas de modifier les prospects.',
  'pr.newTitle': 'Nouveau prospect',
  'pr.newBody': 'Un e-mail ou un téléphone est nécessaire.',
  'pr.fullName': 'Nom complet',
  'pr.birthYear': 'Année de naissance',
  'pr.create': 'Créer',
  'pr.saved': 'Enregistré',
  'pr.overdue': 'en retard',
  'pr.today': 'aujourd’hui',
} satisfies Record<string, string>

type MessageKey = keyof typeof fr

const en: Record<MessageKey, string> = {
  'app.name': 'Sales workspace',
  'app.signOut': 'Sign out',
  'app.language': 'Change language',
  'app.search': 'Search for a customer…',
  'app.retry': 'Try again',
  'app.loading': 'Loading…',
  'app.back': 'Back',
  'app.cancel': 'Cancel',
  'app.none': 'None',

  'nav.home': 'Home',
  'nav.customers': 'Customers',
  'nav.treasury': 'Treasury',
  'nav.pipeline': 'Pipeline',
  'app.refresh': 'Refresh',
  'app.menu': 'Menu',
  'nav.groupMoney': 'Money',
  'nav.groupPeople': 'People',

  /* referrals (2026-09-25) */
  'nav.referrals': 'Referrals',
  'cust.tabDirectory': 'Directory',
  'nav.transfers': 'Transfers',

  'tr.title': 'Customer transfers',
  'tr.subtitle': 'Instant USDT sent from one Tothor customer to another. Final: there is no reversal, and this screen only shows them.',
  'tr.limits': 'Limits: {per} per transfer · {daily} per 24 h per sender',
  'tr.kpiCount': 'Transfers',
  'tr.kpiVolume': 'Total volume',
  'tr.kpi24h': '24 h volume',
  'tr.kpiSenders': 'Senders',
  'tr.colWhen': 'Date',
  'tr.colFrom': 'From',
  'tr.colTo': 'To',
  'tr.colAmount': 'Amount',
  'tr.colNote': 'Message',
  'tr.searchPh': 'Name, email or message…',
  'tr.none': 'No customer transfers yet.',
  'ref.title': 'Referrals',
  'ref.subtitle':
    'Every customer can invite others. The referrer receives {quantity} (tokens locked 12 months, outside the USDT claims) as soon as the invitee’s identity is verified — automatically. This page monitors the programme and can revoke a bonus.',
  'ref.kpiTotal': 'Referrals',
  'ref.kpiPending': 'Awaiting KYC',
  'ref.kpiPaid': 'Bonuses paid',
  'ref.kpiPaidQty': 'TTR given',
  'ref.kpiReferrers': 'Referrers',
  'ref.tabReferrals': 'All referrals',
  'ref.tabReferrers': 'By referrer',
  'ref.colReferrer': 'Referrer',
  'ref.colReferee': 'Invitee',
  'ref.colRefereeKyc': 'Invitee KYC',
  'ref.colStatus': 'Bonus',
  'ref.colBonus': 'Amount',
  'ref.colDate': 'Signed up',
  'ref.colInvited': 'Invited',
  'ref.colPaid': 'Paid',
  'ref.colPending': 'Pending',
  'ref.colRevoked': 'Revoked',
  'ref.colEarned': 'Earned',
  'ref.filterAll': 'All',
  'ref.revoke': 'Revoke bonus',
  'ref.revokePending': 'Block',
  'ref.revokeTitle': 'Revoke this referral',
  'ref.revokeBodyPaid':
    'Takes {quantity} back out of {who}’s savings (a correction row). Cannot be undone from the console. The customer sees “Correction: referral bonus reversed” in their activity; your reason is never shown.',
  'ref.revokeBodyPending':
    'This referral will never pay a bonus, even if the invitee is verified later. Your reason is never shown to the customer.',
  'ref.revokeReason': 'Reason (internal)',
  'ref.revokeReasonPh': 'e.g. duplicate accounts, same person',
  'ref.revokeConfirm': 'Revoke bonus',
  'ref.revokedBy': 'Revoked by {who}: {reason}',
  'ref.suspended': 'suspended',
  'ref.emailUnverified': 'email unverified',
  'ref.searchPh': 'Filter by email, name or code…',
  'status.PAID': 'Paid',
  'status.REVOKED': 'Revoked',

  /* pipeline — the money's path, not any one account's */
  'pipe.title': 'Pipeline',
  'pipe.subtitle':
    'Every hop a customer’s money takes, with the balance sitting at each one right now. Read left to right; a gap between two hops is money in transit.',
  'pipe.usdtIn': 'Direct USDT deposit',
  'pipe.usdtInSub': 'The customer sends to their own address, then a root job moves it to the treasury — no partner custody, no sweep.',
  'pipe.usdtInPending': 'address(es) to move',
  'pipe.hop1': '1 · Partner custody',
  'pipe.hop2': '2 · Tothor treasury',
  'pipe.hop3': '3 · After the swap',
  'pipe.hop4': '4 · Card pool',
  'pipe.hop1sub': 'SwipeLux · not credited',
  'pipe.hop2sub': 'USDC on-chain',
  'pipe.hop3sub': 'USDT spendable',
  'pipe.hop4sub': 'HyperPay · must sit ~0',
  'pipe.sweep': 'sweep',
  'pipe.swap': 'swap',
  'pipe.topup': 'top-up',
  'pipe.owed': 'Owed to customers',
  'pipe.tracked': 'of which tracked:',
  'pipe.held': 'Held in total',
  'pipe.inflight': 'In transit',
  'pipe.stuck': 'Stuck',
  'pipe.openItems': 'Sweeps in flight',
  'pipe.none': 'Nothing in transit — every sweep has landed.',
  'pipe.age': 'Age',
  'pipe.unreadable': 'unreadable',
  'pipe.poolResting':
    'The card pool is holding a resting balance. It is a pass-through and should sit near zero between operations. A resting balance means funds left the treasury without a matching debit.',

  'home.attention': 'Needs you',
  'home.attentionEmpty': 'Nothing waiting. Everything is up to date.',
  'home.attentionNote': 'These customers are waiting on a decision from you.',
  'home.overview': 'Overview',
  'home.totalHeld': 'Total customer balance',

  'seg.needs_verification': 'To verify',
  'seg.no_card': 'No card yet',
  'seg.active': 'Active',
  'seg.dormant': 'Dormant',
  'seg.all': 'Everyone',
  'seg.needs_verification.hint': 'Identity not confirmed (Tothor) or email not verified (1cards) — they cannot do everything yet.',
  'seg.no_card.hint': 'Verified but no card. Worth a call.',
  'seg.active.hint': 'Card in hand and using the account.',
  'seg.dormant.hint': 'No movement in 60 days.',

  'cust.since': 'Customer since {when}',
  'cust.balance': 'Balance',
  'cust.savings': 'Savings (not spendable)',
  'cust.cards': 'Cards',
  'cust.pending': 'Waiting',
  'cust.colCustomer': 'Customer',
  'cust.colKyc': 'Verification',
  'cust.colSpendable': 'Spendable',
  'cust.colTtr': 'TTR token',
  'cust.colGold': 'Gold (oz · g)',
  'cust.colBtc': 'Bitcoin (BTC)',
  'cust.colCards': 'Cards',
  'cust.colJoined': 'Joined',
  'cust.colDeposits': 'Deposits',
  'cust.granted': 'granted',
  'cust.connect': 'Connect',
  'cust.connecting': 'Opening…',
  'cust.noBalance': 'No balance',
  'cust.tab.activity': 'Activity',
  'cust.tab.cards': 'Cards',
  'cust.tab.deposits': 'Deposits',
  'cust.tab.withdrawals': 'Withdrawals',
  'cust.tab.identity': 'Identity',
  'cust.tab.notes': 'Notes',
  'cust.tab.partners': 'Partners',
  'vnd.sumsub': 'Sumsub — identity verification',
  'vnd.swipelux': 'SwipeLux — card deposits',
  'vnd.hypercard': 'HyperCard — card',
  'vnd.readonly': 'read only',
  'vnd.noApplicant': 'No Sumsub record for this customer.',
  'vnd.extracted': 'Read off the documents',
  'vnd.submitted': 'Sent by Tothor',
  'vnd.showDocs': 'Show identity documents',
  'vnd.docsNote': 'Every view is recorded against your name.',
  'vnd.noDocs': 'No image available.',
  'vnd.noSwxCustomer': 'No SwipeLux customer — one is created by the first quote, never at signup.',
  'vnd.custody': 'Held with the partner',
  'vnd.empty': 'Empty.',
  'vnd.more': 'Rails, capabilities and accounts',
  'vnd.txns': '{n} operations (60 days)',
  'vnd.application': 'Card application',
  'vnd.noPan': 'The card number and CVV are not shown here — the cardholder reads them in their app.',
  'cust.noActivity': 'Nothing has happened yet.',
  'cust.noCards': 'No cards.',
  'card.freeze': 'Freeze card',
  'card.unfreeze': 'Unfreeze card',
  'card.freezeTitle': 'Freeze card •••• {last4}?',
  'card.unfreezeTitle': 'Unfreeze card •••• {last4}?',
  'card.freezeBody': 'The request goes to the issuer. Once confirmed, the card declines every payment until it is unfrozen.',
  'card.unfreezeBody': 'The request goes to the issuer. Once confirmed, the card accepts payments again.',
  'card.freezeRequested': 'Freeze requested — the card updates when the issuer confirms.',
  'card.unfreezeRequested': 'Unfreeze requested — the card updates when the issuer confirms.',
  'card.refresh': 'Refresh',
  'card.issuerFrozen': 'Frozen by the issuer — cannot be unfrozen from here.',
  'cust.noDeposits': 'No deposits.',
  'cust.noWithdrawals': 'No withdrawals.',
  'cust.noNotes': 'No notes.',
  'cust.suspended': 'Account suspended',
  'cust.emailUnverified': 'Email not confirmed',
  'cust.earlierReason': 'Reason (earlier attempt)',
  'kyc.restart': 'Restart verification',
  'kyc.restartHint': 'Resets the Sumsub check so the customer sends a new ID document and selfie. A verified customer keeps access until the new verdict. The customer is emailed.',
  'kyc.restartTitle': 'Restart identity verification',
  'kyc.restartBody': 'The customer will photograph their ID and take a selfie again. If already verified, they keep access to their account until the new verdict. They are emailed a request to resend their documents.',
  'kyc.restartNote': 'Internal note (optional)',
  'kyc.restartPlaceholder': 'e.g. blurry photo, expired document',
  'kyc.reopenedNote': 'Verification reopened — waiting for new documents. The customer stays verified meanwhile.',
  'pin.set': 'Set PIN',
  'pin.short': 'PIN',
  'pin.saved': 'PIN saved ✓',
  'pin.change': 'Change PIN',
  'pin.on': 'PIN active',
  'pin.off': 'No PIN',
  'pin.locked': 'PIN locked',
  'pin.done': 'PIN saved — customer notified by email',
  'pin.title': 'Customer’s 6-digit PIN',
  'pin.body': 'Replaces the PIN that unlocks the customer’s app and clears any lockout. The customer is emailed that support changed it. Give them the PIN, then ask them to choose their own.',
  'pin.new': 'New PIN',
  'pin.again': 'Confirm PIN',
  'pin.mismatch': 'The two PINs do not match.',
  'pin.cancel': 'Cancel',
  'pin.save': 'Save PIN',
  'pin.saving': 'Saving…',
  'cust.lastSeen': 'Last activity',
  'cust.never': 'never',
  'cust.contact': 'Contact details',
  'cust.cardEnding': 'Card •••• {last4}',

  'act.approve': 'Approve',
  'act.refuse': 'Refuse',
  'act.autoPaying': 'Paying out automatically on Polygon',
  'act.noPayoutRight': 'Only an administrator can decide this payout',
  'act.autoApproved': 'approved automatically',
  'act.payoutFailed': 'Automatic payout failed — the funds are still held. Retry the payout, or refuse to give them back to the customer.',
  'act.retry': 'Retry payout',
  'act.retryHint': 'A retry is accepted only when the chain confirms nothing was sent.',
  'act.approveIdentity': 'Approve identity',
  'act.reason': 'Reason shown to the customer',
  'act.reasonPlaceholder': 'This destination address could not be verified.',
  'act.confirmRefuse': 'Refuse this withdrawal',
  'act.refuseBody':
    'The money returns to the customer’s balance immediately. They will see the reason you give.',
  'act.available': 'Available balance',
  'act.requestedAgo': 'Requested {age}',
  'act.to': 'To',

  'status.VERIFIED': 'Verified',
  'status.NOT_STARTED': 'Not started',
  'status.PENDING': 'In progress',
  'status.SUBMITTED': 'Documents sent',
  'status.IN_REVIEW': 'Being reviewed',
  'status.REJECTED': 'Refused',
  'status.REQUESTED': 'To approve',
  'status.APPROVED': 'Ready to send',
  'status.SENT': 'Sent',
  'status.SENDING': 'Sending',
  'status.PAYOUT_FAILED': 'Payout failed',
  'status.RETURNED': 'Returned',
  'status.CREDITED': 'Credited',
  'status.COMPLETED': 'Done',
  'status.CANCELLED': 'Cancelled',
  'status.FAILED': 'Failed',
  'status.ACTIVE': 'Active',
  'status.BLOCKED': 'Blocked',

  'kind.payout': 'Withdrawal',
  'kind.identity': 'Identity check',
  'money.in': 'In',
  'money.out': 'Out',

  'signin.subtitle': 'Customer records. Staff only.',
  'signin.email': 'Email',
  'signin.password': 'Password',
  'signin.submit': 'Sign in',
  'signin.working': 'Signing in…',
  'signin.audit': 'Every action is recorded against your name.',

  /* environment banner — absent on production, by design */
  'env.banner': '{env} environment — this is not real data',
  'env.unknown': 'Environment unknown — could not reach the API',

  /* treasury — the money screen. Wording matters here: the shared card pool is
     the CARDHOLDER's money in transit, never Tothor's. */
  'treasury.title': 'Treasury',
  'treasury.subtitle': 'What users are owed, against what Tothor actually holds.',
  'treasury.loading': 'Loading…',
  'treasury.buffer': 'buffer',
  'treasury.owed': 'Users are owed',
  'treasury.onchain': 'Client pool on-chain',
  'treasury.holds': 'Holds in flight',
  'treasury.orphans': 'Unattributed rows',
  'treasury.orphansWarning':
    'Ledger rows with no owner — Tothor holds money it cannot attribute to a user.',
  'treasury.chainUnreadable':
    'an unreadable chain is NOT zero; this figure is unknown, not empty.',
  'treasury.poolTitle': 'Shared card pool — cardholder funds in transit',
  // Carries what the API's own `note` field says, so the console no longer
  // renders that sentence twice in two wordings.
  'treasury.poolNote':
    'Pass-through for cardholder funds — ~0 at rest. It is funded per card request from that cardholder’s own balance and drains as the card is issued, so it IS counted in the solvency total above. There is no company money in this product. ⚠️ A resting balance here means money left the treasury without a matching debit.',
  'treasury.floor': 'floor',
  'treasury.issuingOn': 'card issuing ON',
  'treasury.issuingOff': 'card issuing OFF',
  'treasury.poolEmpty':
    'Empty — no card can be issued or topped up until this is funded, whatever a user’s balance says.',
  'treasury.sweeps': 'Sweeps',
  'treasury.inFlight': '{n} in flight',
  'treasury.noSweeps': 'No sweeps yet.',
  'treasury.thCustomer': 'Customer',
  'treasury.thStatus': 'Status',
  'treasury.thRequested': 'Requested',
  'treasury.thConfirmed': 'Confirmed',
  'treasury.thProof': 'Proof',
  'cust.depositBlocked': 'This customer cannot deposit by card',
  'cust.depositBlockedBody': 'Our payment partner refuses while these are missing:',
  'cust.depositBlockedNote':
    'The customer can fill these in themselves under Settings. USDT deposits are unaffected.',
  'cust.field.street': 'street address',
  'cust.field.city': 'city',
  'cust.field.post_code': 'post code',
  'dep.seenBefore': '· seen before',
  'dep.title': 'USDT deposits',
  'dep.none': 'No USDT deposits yet.',
  'dep.why':
    'Every customer has their own address: a finalized transfer on Polygon credits its owner automatically, with the amount read from the chain. Nothing to confirm here.',
  'dep.from': 'Sent from',
  'dep.to': 'Received at',
  'dep.stock': 'Addresses ready',
  'dep.assigned': 'Addresses assigned',
  'dep.awaitingForward': 'Awaiting transfer to the treasury',
  'dep.stockEmpty':
    'No addresses ready: new customers cannot be given a deposit address until deposit_forward.py (root crontab) has run.',
  'dep.inAddresses': 'of which in customer deposit addresses',
  'dep.address': 'USDT deposit address',
  'dep.noAddress': 'No address yet — the customer has not opened the USDT deposit page.',
  'dep.forwardPending': 'USDT credited, not yet moved to the treasury',
  'treasury.retry': 'Retry',
  'treasury.retrying': 'Retrying…',

  /* 🔴 Savings is price-tracked: the customer takes back their QUANTITY at the
     rate on the day, so this part of the claim is backed by nothing in the
     treasury. Broken out so an operator reading a buffer knows how much of it
     can move overnight on its own. */
  'treasury.claimUnknown': 'unknown',
  'treasury.claimUnknownNote':
    'while a savings position cannot be priced, what users are owed is unknown — it is not zero.',
  'treasury.trackedTitle': 'Of which price-tracked savings:',
  'treasury.grantedTitle': 'Granted tokens (not bought — owed in tokens, not in the USDT claim):',
  'treasury.trackedNote':
    'NOT backed by the treasury. Tothor carries the price risk: if gold rises, the claim rises with it.',
  'treasury.unpriceable': 'no price',

  /* solvency verdict — a server enum, so it goes through the dictionary too */
  'verdict.SOLVENT': 'SOLVENT',
  'verdict.SHORTFALL': 'SHORTFALL',
  'verdict.UNKNOWN': 'UNKNOWN',

  /* sweep states — see the French block for why these are not `status.*` */
  'sweep.PENDING': 'Created — nothing sent',
  'sweep.QUOTED': 'Quoted',
  'sweep.SUBMITTED': 'Submitted to partner',
  'sweep.SENT': 'Sent — not confirmed',
  'sweep.CONFIRMED': 'Confirmed on-chain',
  'sweep.ACTION_REQUIRED': 'Action required',
  'sweep.NEEDS_REVIEW': 'Needs review',
  'sweep.FAILED': 'Failed',
  'sweep.ABANDONED': 'Abandoned',

  /* brands (2026-10-14) — the console's Global / Tothor / 1cards view */
  'brand.view': 'Brand shown',
  'brand.all': 'Global',
  'brand.tothor': 'Tothor',
  'brand.1cards': '1cards',
  'brand.sharedPot': '{brand} view: what is owed is what {brand} customers are owed. The treasury and the card pool are shared by both brands — they read the same in every view, and only the Global view says whether they cover what is owed.',
  'brand.noVerdict': 'No solvency verdict outside the Global view',
  'brand.noKyc': '1cards does not verify identity',

  /* team — operator accounts (2026-10-14), SUPER_ADMIN only */
  'nav.team': 'Team',
  'nav.account': 'My account',
  'team.title': 'Team',
  'team.subtitle': 'The accounts that can use this console: who they are, their role, what that role allows, and what they have done.',
  'team.tabOperators': 'Operators',
  'team.tabRoles': 'Roles and permissions',
  'team.new': 'New operator',
  'team.kpiActive': 'Active accounts',
  'team.kpiSuper': 'Super admins',
  'team.kpiInactive': 'Deactivated',
  'team.searchPh': 'Name or email…',
  'team.colOperator': 'Operator',
  'team.colRole': 'Role',
  'team.colStatus': 'Status',
  'team.colLastLogin': 'Last sign-in',
  'team.colActions': 'Actions',
  'team.never': 'Never',
  'team.you': 'you',
  'team.active': 'Active',
  'team.inactive': 'Deactivated',
  'team.open': 'Open',
  'team.actionsCount': '{n} action(s)',
  'team.rolesIntro': 'Permissions belong to the role, not the person: changing an operator’s role changes everything they can do, from their next request. Only permissions that a screen or action actually checks are listed.',
  'team.permission': 'Permission',
  'team.allows': 'What it allows',

  'role.SUPER_ADMIN': 'Super admin',
  'role.ADMIN': 'Admin',
  'role.SUPPORT': 'Support',
  'role.VIEWER': 'Read only',
  'roleDesc.SUPER_ADMIN': 'Everything, including treasury movements and managing the team.',
  'roleDesc.ADMIN': 'The daily work: customers, KYC, withdrawals. Neither treasury nor team.',
  'roleDesc.SUPPORT': 'Answering customers: see their file and documents, decide nothing.',
  'roleDesc.VIEWER': 'See everything — customers, treasury — change nothing.',

  'perm.users:read': 'See customers',
  'perm.users:write': 'Act on a customer account',
  'perm.kyc:read': 'See identity documents',
  'perm.kyc:write': 'Decide a verification',
  'perm.cards:write': 'Act on a card',
  'perm.finance:read': 'See the treasury',
  'perm.withdrawals:write': 'Decide a withdrawal',
  'perm.finance:write': 'Treasury movements',
  'perm.admins:write': 'Manage the team',
  'permDesc.users:read': 'Directory, customer page, partner data, referrals.',
  'permDesc.users:write': 'Connect as the customer, set their PIN.',
  'permDesc.kyc:read': 'Sumsub verification data and photos.',
  'permDesc.kyc:write': 'Approve a file, restart a verification.',
  'permDesc.cards:write': 'Ask the issuer to freeze or unfreeze a card.',
  'permDesc.finance:read': 'Treasury, money pipeline, transfers, deposits.',
  'permDesc.withdrawals:write': 'Approve, refuse or retry a withdrawal.',
  'permDesc.finance:write': 'Retry a sweep, revoke a referral bonus.',
  'permDesc.admins:write': 'This screen: create, edit, deactivate operators.',

  'op.back': 'Team',
  'op.profile': 'Profile',
  'op.firstName': 'First name',
  'op.lastName': 'Last name',
  'op.email': 'Email',
  'op.role': 'Role',
  'op.save': 'Save',
  'op.saved': 'Saved.',
  'op.created': 'Created {date}',
  'op.lastLogin': 'Last sign-in: {date}',
  'op.access': 'Access',
  'op.activeBody': 'This account can sign in.',
  'op.inactiveBody': 'This account can no longer sign in. Its history stays in its name.',
  'op.deactivate': 'Deactivate',
  'op.reactivate': 'Reactivate',
  'op.deactivateTitle': 'Deactivate {name}?',
  'op.deactivateBody': 'Access is cut immediately: their next request is refused. Their history stays intact and you can reactivate them.',
  'op.password': 'Password',
  'op.passwordBody': 'Give this operator a new password, then pass it on through a safe channel. A session already open is not cut (it expires within 15 minutes): if the account is compromised, deactivate it first.',
  'op.passwordNew': 'New password',
  'op.passwordSet': 'Set password',
  'op.passwordDone': 'Password changed. Pass it on to the operator.',
  'op.generate': 'Generate',
  'op.copy': 'Copy',
  'op.copied': 'Copied',
  'op.passwordMin': 'At least {n} characters.',
  'op.activity': 'Activity',
  'op.activityNone': 'No recorded action.',
  'op.delete': 'Delete the account',
  'op.deleteBody': 'Only for an account created by mistake and never used. An operator who has signed in or acted is deactivated instead: deleting would make their history anonymous.',
  'op.deleteConfirmTitle': 'Delete {name}?',
  'op.deleteConfirmBody': 'Permanent. Type the operator’s email address to confirm.',
  'op.deleteBlocked': 'This account has a history: deactivate it instead.',
  'op.self': 'This is your account: your role and access are changed by another super admin. Your name and password are changed in My account.',
  'op.newTitle': 'New operator',
  'op.newBody': 'The account is active as soon as it is created. Pass the password on through a safe channel.',
  'op.create': 'Create the account',

  'acct.title': 'My account',
  'acct.subtitle': 'Your name, your password, and what your role lets you do.',
  'acct.yourRole': 'Your role',
  'acct.currentPassword': 'Current password',
  'acct.newPassword': 'New password',
  'acct.confirmPassword': 'Confirm the new password',
  'acct.mismatch': 'The two passwords do not match.',
  'acct.change': 'Change password',
  'acct.changed': 'Password changed. Use it the next time you sign in.',

  'act.ADMIN_LOGIN': 'Signed in',
  'act.ADMIN_CREATE': 'Created an operator',
  'act.ADMIN_UPDATE': 'Edited an operator',
  'act.ADMIN_DELETE': 'Deleted an operator',
  'act.ADMIN_PASSWORD_RESET': 'Changed an operator’s password',
  'act.ADMIN_PASSWORD_CHANGE': 'Changed their own password',
  'act.ADMIN_PROFILE_UPDATE': 'Edited their own profile',
  'act.KYC_APPLICATION_APPROVE': 'Approved a KYC',
  'act.KYC_APPLICATION_REJECT': 'Rejected a KYC',
  'act.KYC_DOCUMENTS_VIEW': 'Viewed identity documents',
  'act.REFERRAL_REVOKE': 'Revoked a referral bonus',
  'act.USER_EMAIL_CHANGE': 'Changed a customer’s email',
  'act.USER_EMAIL_VENDOR_REPAIR': 'Repaired a customer’s email at a partner',
  'act.USER_FORCE_VERIFY_KYC': 'Force-verified a customer',
  'act.USER_IMPERSONATE': 'Connected as a customer',
  'act.USER_KYC_RESTART': 'Restarted a customer’s verification',
  'act.USER_PIN_SET': 'Set a customer’s PIN',
  'act.CARD_FREEZE_REQUESTED': 'Requested a card freeze',
  'act.CARD_UNFREEZE_REQUESTED': 'Requested a card unfreeze',
  'act.USER_SUSPEND': 'Suspended a customer',
  'act.USER_UNSUSPEND': 'Unsuspended a customer',

  // Prospects (2026-11-01)
  'nav.groupSales': 'Sales',
  'nav.prospects': 'Prospects',
  'perm.prospects:read': 'See prospects',
  'perm.prospects:write': 'Work prospects',
  'permDesc.prospects:read': 'Tothor’s prospect list, figures and history.',
  'permDesc.prospects:write': 'Notes, status, follow-ups.',
  'pr.title': 'Prospects',
  'pr.subtitle': 'The contacts the sales team is working — not customers yet. Tothor’s own.',
  'pr.count': '{n} prospects',
  'pr.add': 'Add a prospect',
  'pr.onlyTothor': 'Prospects belong to Tothor',
  'pr.onlyTothorBody': 'This space exists only in the Tothor view — not in Global, not at 1cards.',
  'pr.switchTothor': 'Switch to the Tothor view',
  'pr.kpiTotal': 'Prospects',
  'pr.kpiDue': 'Follow-ups due',
  'pr.ofTotal': '{p} of total',
  'pr.dueHint': 'due today or overdue',
  'pr.accountNotMarked': '{n} prospect(s) opened a Tothor account but are marked neither “Account opened” nor converted.',
  'pr.showThem': 'Show them',
  'pr.chStatus': 'By status',
  'pr.chActivity': 'Activity — last 30 days',
  'pr.chActivitySub': 'Notes and changes recorded per day.',
  'pr.chAge': 'By age',
  'pr.chAgeSub': 'Worked out from the file’s birth year.',
  'pr.seriesNotes': 'Notes',
  'pr.seriesChanges': 'Changes',
  'pr.seriesConversions': 'conversion(s)',
  'pr.ageUnknown': 'Unknown',
  'pr.tableView': 'Show the numbers',
  'pr.chartView': 'Show the chart',
  'pr.searchPh': 'Name, email, phone…',
  'pr.filterAll': 'All',
  'pr.onlyDue': 'Follow-ups due',
  'pr.onlyAccount': 'Have an account',
  'pr.sortBy': 'Sort by',
  'pr.sortUpdated': 'Last updated',
  'pr.sortCreated': 'Date added',
  'pr.colCreated': 'Added',
  'pr.colName': 'Prospect',
  'pr.colPhone': 'Phone',
  'pr.colAge': 'Age',
  'pr.colStatus': 'Status',
  'pr.colFollowUp': 'Follow-up',
  'pr.colNotes': 'Notes',
  'pr.colUpdated': 'Updated',
  'pr.none': 'No prospects.',
  'pr.noMatch': 'No prospect matches.',
  'pr.page': '{from}–{to} of {n}',
  'pr.prev': 'Previous',
  'pr.next': 'Next',
  'pr.years': '{n} yrs',
  'pr.hasAccount': 'Has an account',
  'pr.status.NEW': 'To call',
  'pr.status.NRP_1': 'Didn’t answer 1',
  'pr.status.NRP_2': 'Didn’t answer 2',
  'pr.status.NRP_3': 'Didn’t answer 3',
  'pr.status.INTERESTED_HIGH': 'Interested — high potential',
  'pr.status.INTERESTED_LOW': 'Interested — low potential',
  'pr.status.INTERESTED': 'Interested (to qualify)',
  'pr.status.CALLBACK': 'Call back',
  'pr.status.ACCOUNT_OPENED': 'Account opened',
  'pr.status.CONVERTED': 'Converted',
  'pr.status.NOT_INTERESTED': 'Not interested',
  'pr.status.INVALID': 'Invalid details',
  // Statuses and interests from before 2026-11-03 — the timeline still carries them.
  'pr.status.NO_ANSWER': 'No answer',
  'pr.status.CONTACTED': 'Contacted',
  'pr.status.FOLLOW_UP': 'Follow up',
  'pr.status.MEETING': 'Meeting',
  'pr.status.CLOSED': 'Closed',
  'pr.interest.UNKNOWN': 'Unknown',
  'pr.interest.INTERESTED': 'Interested',
  'pr.interest.NOT_INTERESTED': 'Not interested',
  'pr.markAccountOpened': 'Mark “Account opened”',
  'pr.back': 'Prospects',
  'pr.contact': 'Contact',
  'pr.email': 'Email',
  'pr.phone': 'Phone',
  'pr.birth': 'Born',
  'pr.source': 'Source',
  'pr.added': 'Added',
  'pr.lastContact': 'Last contact',
  'pr.pipeline': 'Pipeline',
  'pr.status': 'Status',
  'pr.interest': 'Interest',
  'pr.followUp': 'Next follow-up',
  'pr.clearFollowUp': 'Clear',
  'pr.accountOpened': 'This prospect has opened a Tothor account.',
  'pr.openClient': 'Open the client page',
  'pr.notes': 'Notes and history',
  'pr.notePh': 'Call, answer, objection, next step…',
  'pr.addNote': 'Add note',
  'pr.noActivity': 'No activity.',
  'pr.by': 'by {who}',
  'pr.system': 'import',
  'pr.act.CREATED': 'Added to the list ({src})',
  'pr.act.STATUS': 'Status: {from} → {to}',
  'pr.act.INTEREST': 'Interest: {from} → {to}',
  'pr.act.CONVERTED_ON': 'Marked converted to client',
  'pr.act.CONVERTED_OFF': 'Conversion removed',
  'pr.act.FOLLOW_UP': 'Follow-up set for {to}',
  'pr.act.FOLLOW_UP_CLEAR': 'Follow-up cleared',
  'pr.readOnly': 'Read only — your role cannot change prospects.',
  'pr.newTitle': 'New prospect',
  'pr.newBody': 'An email or a phone number is required.',
  'pr.fullName': 'Full name',
  'pr.birthYear': 'Birth year',
  'pr.create': 'Create',
  'pr.saved': 'Saved',
  'pr.overdue': 'overdue',
  'pr.today': 'today',
}

const DICTS: Record<Locale, Record<string, string>> = { fr, en }
const KEY = 'tothor.ops.locale'

export const locale = ref<Locale>(
  ((): Locale => {
    const s = localStorage.getItem(KEY)
    return s === 'en' || s === 'fr' ? s : 'fr'
  })(),
)

export function setLocale(next: Locale): void {
  locale.value = next
  localStorage.setItem(KEY, next)
  document.documentElement.lang = next
}

export function t(key: string, params?: Record<string, string | number>): string {
  let out = DICTS[locale.value][key] ?? DICTS.en[key] ?? key
  if (params) for (const [k, v] of Object.entries(params)) out = out.replaceAll(`{${k}}`, String(v))
  return out
}

/** Server enums never reach the screen raw. */
export function statusLabel(raw: string | null | undefined): string {
  if (!raw) return '—'
  return t(`status.${raw}`)
}

/**
 * The same rule for the two enums the Treasury screen shows.
 *
 * They had been rendering raw (`SHORTFALL`, `NEEDS_REVIEW`) because that screen
 * never went through the dictionary at all — added 2026-08-07.
 *
 * `t()` falls back to the key itself when a term is missing, which would put
 * `sweep.QUOTED` on screen. For a money screen an unrecognised state must stay
 * legible, so an unknown value falls back to the RAW enum instead: ugly, but it
 * is what the server actually said.
 */
function enumLabel(ns: string, raw: string | null | undefined): string {
  if (!raw) return '—'
  const out = t(`${ns}.${raw}`)
  return out === `${ns}.${raw}` ? raw : out
}

export const verdictLabel = (raw: string | null | undefined) => enumLabel('verdict', raw)
export const sweepLabel = (raw: string | null | undefined) => enumLabel('sweep', raw)
export const actionLabel = (raw: string | null | undefined) => enumLabel('act', raw)

document.documentElement.lang = locale.value
